import { Component, output, input, inject, signal, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { CustomInputComponent } from '../../inputs/custom-input/custom-input.component';
import { CustomButtonComponent } from '../../inputs/custom-button/custom-button.component';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthStore } from '../../../store/auth';

// Custom Validator for matching passwords
export function passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
  const password = control.get('newPassword');
  const confirmPassword = control.get('confirmPassword');

  if (password && confirmPassword && password.value !== confirmPassword.value) {
    confirmPassword.setErrors({ passwordsMismatch: true });
    return { passwordsMismatch: true };
  } else {
    if (confirmPassword) {
      // Clear error if they match or one is empty
      const errors = confirmPassword.errors;
      if (errors && errors['passwordsMismatch']) {
        delete errors['passwordsMismatch'];
        if (Object.keys(errors).length === 0) {
          confirmPassword.setErrors(null);
        } else {
          confirmPassword.setErrors(errors);
        }
      }
    }
    return null;
  }
}

@Component({
  selector: 'lib-recover-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomInputComponent,
    CustomButtonComponent,
    TranslateModule,
  ],
  templateUrl: './recover-password.component.html',
})
export class RecoverPasswordComponent implements OnDestroy {
  featureImageUrl = input<string | null>('public/shared/images/background-login.jpg');
  /** Tailwind color class for image column background */
  backgroundTone = input<string>('bg-blue-950');
  recoverPasswordSubmit = output<{ newPassword: string; token: string | null }>();
  recoverPasswordForm: FormGroup;

  // Señales internas para el manejo del estado
  loading = signal<boolean>(false);
  passwordResetSuccess = signal<boolean>(false);

  // Token para recuperar contraseña
  private _token: string | null = null;
  private _destroy$ = new Subject<void>();
  private _actions$ = inject(Actions);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);

  constructor(private _fb: FormBuilder) {
    this.recoverPasswordForm = this._fb.group(
      {
        newPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/),
          ],
        ],
        confirmPassword: ['', Validators.required],
      },
      { validators: passwordsMatchValidator }
    );

    // Escuchar eventos de éxito y error para actualizar el estado de la UI
    this._actions$
      .pipe(
        ofType(AuthStore.actions.recoverPasswordSuccess, AuthStore.actions.recoverPasswordFailure),
        takeUntil(this._destroy$)
      )
      .subscribe((action) => {
        this.loading.set(false);
        if (action.type === AuthStore.actions.recoverPasswordSuccess.type) {
          this.passwordResetSuccess.set(true);
        }
      });

    this._token = this._route.snapshot.queryParamMap.get('code');
  }

  /**
   * Método para actualizar el estado de carga
   * @param isLoading Estado de carga
   */
  setLoading(isLoading: boolean): void {
    this.loading.set(isLoading);
  }

  /**
   * Método para actualizar el estado de éxito
   * @param isSuccess Estado de éxito
   */
  setPasswordResetSuccess(isSuccess: boolean): void {
    this.passwordResetSuccess.set(isSuccess);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  onSubmit(): void {
    if (this.recoverPasswordForm.valid && !this.loading()) {
      this.loading.set(true);

      // Emitir el evento al componente padre
      this.recoverPasswordSubmit.emit({
        newPassword: this.recoverPasswordForm.value.newPassword,
        token: this._token,
      });
    } else {
      this.recoverPasswordForm.markAllAsTouched();
    }
  }

  get newPassword() {
    return this.recoverPasswordForm.get('newPassword');
  }

  get confirmPassword() {
    return this.recoverPasswordForm.get('confirmPassword');
  }

  goToLogin(): void {
    this._router.navigate(['/login']);
  }
}
